import { useSupabaseClient } from "@supabase/auth-helpers-react";
import { useQueryClient } from "@tanstack/react-query";
import useGetUserMe from "@/hooks/useGetUserMe";
import { useRouter } from "next/router";
import { renderNoData } from "./RenderQ";
import { getFullName, getFullNameFa } from "@/utils/formatter";
import { SyntheticEvent, useEffect, useState } from "react";
import { calcPackageCost, checkCouponStatus } from "@/lib/utils";
import { CreditCard, PaymentForm } from "react-square-web-payments-sdk";
import Modal from "./ui/modal";
import Snack from "./ui/Snack";
import Button from "./ui/button";
import Space from "./ui/Space";
import Input from "./ui/Input";
import React from "react";
import Select from "./ui/Select";
import { En, Fa } from "./ui/multilang";
import { sendGTMEvent } from "@next/third-parties/google";
import RadioButton from "./ui/RadioButton";

const BookingModalContent = ({
  courseQ,
  prevCourseQ,
  setIsIntroModalOpen,
  isActive,
  setDisabledClosing,
  profileId,
}) => {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { locale } = router;
  const supabase = useSupabaseClient();
  const userMeQ = useGetUserMe();
  const [theCourse, setTheCourse] = useState(null);
  const [price, setPrice] = useState(0);
  const [priceWithoutDiscount, setPriceWithoutDiscount] = useState(0);
  const [debt, setDebt] = useState(0);
  const [paidDebt, setPaidDebt] = useState(0);
  const [paidTotal, setPaidTotal] = useState(0);
  const [numberOfDebtSessions, setNumberOfDebtSessions] = useState(0);
  const [isPayingDebtOnly, setIsPayingDebtOnly] = useState(true);
  const [numberOfSessions, setNumberOfSessions] = useState(0);
  const [couponCode, setCouponCode] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [couponStatus, setCouponStatus] = useState({ amount: 0, percent: 0, message: "" });
  const [isPayButtonLoading, setIsPayButtonLoading] = useState<undefined | boolean>(undefined);
  const [errorHint, setErrorHint] = useState("");
  const [paymentHint, setPaymentHint] = useState("");

  useEffect(() => {
    if (prevCourseQ.data && prevCourseQ.data.length > 0) {
      setTheCourse(prevCourseQ.data.filter((c) => c.isExpired === false)[0]);
    }
  }, [prevCourseQ.data]);

  const [addedRecord, setAddedRecord] = useState(null);

  const handlePaymentSuccess = async (orderId: string, last4: string) => {
    const now = new Date();
    const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString().split("T")[0];
    const dateTimeString = now.toISOString().substring(0, 16);

    if (isPayingDebtOnly) {
      setPaidDebt(debt);
      numberOfDebtSessions;
      setPaidTotal(debt);
      const paymentByPayer = {
        date: dateTimeString,
        payerId: theCourse.payerId,
        amountBeforeDiscountsInCAD: price,
        method: "Website",
        amountActuallyPaidInCAD: price,
        paymentDetails: orderId,
        nameOnTheCard: last4,
      };
      const { error: paymentByPayerError } = await supabase.from("paymentByPayer").insert(paymentByPayer);
      if (paymentByPayerError) {
        const { error: paymentByPayerSecondError } = await supabase.from("paymentByPayer").insert(paymentByPayer);
        if (paymentByPayerSecondError) {
          setErrorHint(
            locale === "fa"
              ? "پرداخت شما با موفقیت دریافت شد! 🎉 نیازی به اقدام برای پرداخت مجدد نیست، اما لطفاً برای رفع یک مشکل در رابطه با این پرداخت، با تیم پشتیبانی ما تماس بگیرید."
              : "Your payment has been successfully received! 🎉 There’s no need to try the payment again but please reach out to our support team to fix a problem related to this transaction."
          );
          throw new Error(paymentByPayerSecondError.message);
        }
      }

      const withDebtCourses = prevCourseQ.data
        .filter(
          (pc) =>
            pc.session.length > 0 &&
            pc.session.some((session) => session.packagePurchasedId === null && session.isHeld !== "CON")
        )
        .map((pc) => ({
          ...pc,
          session: pc.session.filter((session) => session.packagePurchasedId === null && session.isHeld !== "CON"),
        }));

      for (const withDebtCourse of withDebtCourses) {
        const sessionCount = withDebtCourse.session.length;
        const purchaseAfterDebtPaymentResponse = await fetch("/api/purchase-after-debt-payment", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            datePackagePurchased: dateTimeString,
            numberOfSessions: sessionCount,
            courseId: withDebtCourse.id,
            expertiseId: withDebtCourse.teacher.expertise.find(
              (exp) => exp.startDate <= todayDate && (exp.endDate === null || exp.endDate > todayDate)
            ).id,
          }),
        });

        const purchaseAfterDebtPaymentResult = await purchaseAfterDebtPaymentResponse.json();

        if (!purchaseAfterDebtPaymentResponse.ok) {
          setErrorHint(
            locale === "fa"
              ? "پرداخت شما با موفقیت دریافت شد! 🎉 نیازی به اقدام برای پرداخت مجدد نیست، اما لطفاً برای رفع یک مشکل در رابطه با این پرداخت، با تیم پشتیبانی ما تماس بگیرید."
              : "Your payment has been successfully received! 🎉 There’s no need to try the payment again but please reach out to our support team to fix a problem related to this transaction."
          );
          console.error(purchaseAfterDebtPaymentResult.error.message);
        }
      }
      setPaymentHint("");
      setAddedRecord(theCourse);

      const sendReceiptResponse = await fetch("/api/sendReceipt", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          paidDebt: debt,
          paidDebtSessionCount: numberOfDebtSessions,
          paidTotal: debt,
          tutor: getFullName(courseQ.data.teacher.user),
          payerName: userMeQ.data.firstname,
          payerId: userMeQ.data.id,
        }),
      });
      queryClient.invalidateQueries({ queryKey: ["bookingPrevCourses-2", profileId] });

      const sendReceiptResult = await sendReceiptResponse.json();

      if (!sendReceiptResponse.ok) {
        console.error(sendReceiptResult.error.message);
      }
      return;
    }

    if (price !== priceWithoutDiscount) {
      const paymentByPayer = {
        date: dateTimeString,
        payerId: theCourse.payerId,
        amountBeforeDiscountsInCAD: priceWithoutDiscount,
        method: "Website",
        amountActuallyPaidInCAD: price,
        paymentDetails: orderId,
        nameOnTheCard: last4,
        couponId: coupon[0].id,
      };
      const { error: paymentByPayerError } = await supabase.from("paymentByPayer").insert(paymentByPayer);
      if (paymentByPayerError) {
        const { error: paymentByPayerSecondError } = await supabase.from("paymentByPayer").insert(paymentByPayer);
        if (paymentByPayerSecondError) {
          setErrorHint(
            locale === "fa"
              ? "پرداخت شما با موفقیت دریافت شد! 🎉 نیازی به اقدام برای پرداخت مجدد نیست، اما لطفاً برای رفع یک مشکل در رابطه با این پرداخت، با تیم پشتیبانی ما تماس بگیرید."
              : "Your payment has been successfully received! 🎉 There’s no need to try the payment again but please reach out to our support team to fix a problem related to this transaction."
          );
          throw new Error(paymentByPayerSecondError.message);
        }
      }

      const usedCoupon = coupon[0];
      usedCoupon.condition.usedCount += 1;
      usedCoupon.condition.usedUser.push(userMeQ.data.payer.id);
      const { error } = await supabase
        .from("coupon")
        .update({ condition: usedCoupon.condition })
        .eq("code", usedCoupon.code);

      if (error) {
        throw new Error(error.message);
      }
    } else {
      const paymentByPayer = {
        date: dateTimeString,
        payerId: theCourse.payerId,
        amountBeforeDiscountsInCAD: price,
        method: "Website",
        amountActuallyPaidInCAD: price,
        paymentDetails: orderId,
        nameOnTheCard: last4,
      };
      const { error: paymentByPayerError } = await supabase.from("paymentByPayer").insert(paymentByPayer);
      if (paymentByPayerError) {
        const { error: paymentByPayerSecondError } = await supabase.from("paymentByPayer").insert(paymentByPayer);
        if (paymentByPayerSecondError) {
          setErrorHint(
            locale === "fa"
              ? "پرداخت شما با موفقیت دریافت شد! 🎉 نیازی به اقدام برای پرداخت مجدد نیست، اما لطفاً برای رفع یک مشکل در رابطه با این پرداخت، با تیم پشتیبانی ما تماس بگیرید."
              : "Your payment has been successfully received! 🎉 There’s no need to try the payment again but please reach out to our support team to fix a problem related to this transaction."
          );
          throw new Error(paymentByPayerSecondError.message);
        }
      }
    }

    if (debt > 0) {
      setPaidDebt(debt);
      const withDebtCourses = prevCourseQ.data
        .filter((pc) => pc.session.length > 0 && pc.session.some((session) => session.packagePurchasedId === null))
        .map((pc) => ({
          ...pc,
          session: pc.session.filter((session) => session.packagePurchasedId === null),
        }));

      for (const withDebtCourse of withDebtCourses) {
        const sessionCount = withDebtCourse.session.length;
        const purchaseAfterDebtPaymentResponse = await fetch("/api/purchase-after-debt-payment", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            datePackagePurchased: dateTimeString,
            numberOfSessions: sessionCount,
            courseId: withDebtCourse.id,
            expertiseId: withDebtCourse.teacher.expertise.find(
              (exp) => exp.startDate <= todayDate && (exp.endDate === null || exp.endDate > todayDate)
            ).id,
          }),
        });

        const purchaseAfterDebtPaymentResult = await purchaseAfterDebtPaymentResponse.json();

        if (!purchaseAfterDebtPaymentResponse.ok) {
          setErrorHint(
            locale === "fa"
              ? "پرداخت شما با موفقیت دریافت شد! 🎉 نیازی به اقدام برای پرداخت مجدد نیست، اما لطفاً برای رفع یک مشکل در رابطه با این پرداخت، با تیم پشتیبانی ما تماس بگیرید."
              : "Your payment has been successfully received! 🎉 There’s no need to try the payment again but please reach out to our support team to fix a problem related to this transaction."
          );
          console.error(purchaseAfterDebtPaymentResult.error.message);
        }
      }
    }
    setPaidTotal(price);

    const packagePurchased = {
      courseId: theCourse.id,
      expertiseId: theCourse.teacher.expertise.find(
        (exp) => exp.startDate <= todayDate && (exp.endDate === null || exp.endDate > todayDate)
      ).id,
      datePackagePurchased: dateTimeString,
      numberOfSessions,
    };

    const { error: packageError } = await supabase.from("packagePurchased").insert(packagePurchased);
    if (packageError) {
      const { error: packageSecondError } = await supabase.from("packagePurchased").insert(packagePurchased);
      if (packageSecondError) {
        setErrorHint(
          locale === "fa"
            ? "پرداخت شما با موفقیت دریافت شد! 🎉 نیازی به اقدام برای پرداخت مجدد نیست، اما لطفاً برای رفع یک مشکل کوچک در رابطه با این پرداخت، با تیم پشتیبانی ما تماس بگیرید."
            : "Your payment has been successfully received! 🎉 There’s no need to try the payment again but please reach out to our support team to fix a small problem related to this transaction."
        );
        throw new Error(packageError.message);
      }
    }
    setAddedRecord(theCourse);

    const sendReceiptResponse = await fetch("/api/sendReceipt", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        paidDebt: debt,
        paidDebtSessionCount: numberOfDebtSessions,
        paidTotal: price,
        numberOfSessions,
        tutor: getFullName(courseQ.data.teacher.user),
        payerName: userMeQ.data.firstname,
        payerId: userMeQ.data.id,
        students: theCourse.courseStudent.map((cs) => cs.student.first_name + " " + cs.student.last_name).join(", "),
        subject: courseQ.data.topic.name,
      }),
    });

    queryClient.invalidateQueries({ queryKey: ["bookingPrevCourses-2", profileId] });
    const sendReceiptResult = await sendReceiptResponse.json();
    if (!sendReceiptResponse.ok) {
      console.error(sendReceiptResult.error.message);
    }
  };

  const onApplyCoupon = async (e: SyntheticEvent) => {
    e.preventDefault();
    const { data, error } = await supabase.from("coupon").select(`id, code, condition`).eq("code", couponCode);
    if (error) {
      console.error(error);
    }

    setCoupon(data);
    const courseInfo = {
      categoryId: courseQ.data.categoryId,
      teacherId: courseQ.data.teacherId,
      topicId: courseQ.data.topicId,
    };
    setCouponStatus(checkCouponStatus(data, userMeQ.data, courseInfo, numberOfSessions));
  };

  useEffect(() => {
    const withDebtCourses = prevCourseQ.data
      .filter(
        (pc) =>
          pc.session.length > 0 &&
          pc.session.some((session) => session.packagePurchasedId === null && session.isHeld !== "CON")
      )
      .map((pc) => ({
        ...pc,
        session: pc.session.filter((session) => session.packagePurchasedId === null && session.isHeld !== "CON"),
      }));

    let debtSessionCount = 0;

    const totalDebt = withDebtCourses.reduce((sum, course) => {
      const numberOfSessions = course.session.length;
      debtSessionCount += numberOfSessions;
      const courseDebt = calcPackageCost(course, numberOfSessions, courseQ.data.topicId, true);
      return sum + (courseDebt || 0); // Ensure we add 0 if calcPackageCost returns null
    }, 0);

    setNumberOfDebtSessions(debtSessionCount);
    setDebt(totalDebt);
  }, [prevCourseQ.data, courseQ.data.topicId]);

  useEffect(() => {
    if (theCourse) {
      const basePrice = calcPackageCost(theCourse, numberOfSessions, courseQ.data.topicId);
      const totalPrice = (basePrice || 0) + debt;
      setPriceWithoutDiscount(Math.ceil(totalPrice * 100) / 100);

      if (couponStatus.amount > 0) {
        setPrice(Math.ceil((totalPrice - couponStatus.amount) * 100) / 100);
      } else if (couponStatus.percent > 0) {
        setPrice(Math.ceil(totalPrice * (1 - couponStatus.percent / 100) * 100) / 100);
      } else {
        setPrice(Math.ceil(totalPrice * 100) / 100);
      }
    }
  }, [numberOfSessions, couponStatus, theCourse, debt, courseQ]);

  if (prevCourseQ.data && prevCourseQ.data.length === 0) {
    return (
      <>
        <h2 className="H2 mb-4">{locale === "fa" ? "رزرو و پرداخت" : "Booking and Payment"}</h2>
        <Snack variant="info">
          <Fa>
            <p className="">
              نیاز است که در ابتدا درخواست جلسه معرفی با این معلم را ثبت کنید و پس از آن می‌توانید پرداخت و رزرو را
              انجام دهید.{" "}
            </p>
          </Fa>
          <En>
            <p className="">
              You need to get an introductory meeting for this tutor first. After that you can reserve sessions.{" "}
            </p>
          </En>
        </Snack>
        <Space size="h-6" />
        <Modal.Close asChild>
          <Button
            variation="solid-prm"
            width="parent"
            disabled={!isActive}
            onClick={() => {
              setIsIntroModalOpen(true);
              sendGTMEvent({ event: "request-pay-modal-click" });
            }}
            className="intro-request-pay-button"
          >
            <Fa>درخواست جلسه معرفی رایگان</Fa>
            <En>Request a Free Introductory Meeting</En>
          </Button>
        </Modal.Close>
      </>
    );
  }

  return (
    <>
      <h2 className="H3">{locale === "fa" ? "رزرو و پرداخت" : "Booking and Payment"}</h2>
      {renderNoData(courseQ) ?? renderNoData(userMeQ) ?? renderNoData(prevCourseQ) ?? (
        <>
          <p>
            {locale === "fa" ? "معلم" : "Tutor"}:{" "}
            {locale === "fa" ? getFullNameFa(courseQ.data.teacher.user) : getFullName(courseQ.data.teacher.user)}
          </p>
          <p>
            {locale === "fa" ? "موضوع" : "Subject"}:{" "}
            {locale === "fa" ? courseQ.data.topic.nameFa : courseQ.data.topic.name}
          </p>
          <Space size="h-6" />
          {!addedRecord && debt > 0 && (
            <>
              <Snack variant="warning">
                <En>
                  <p>
                    You currently have {numberOfDebtSessions} unpaid sessions totaling {Math.ceil(debt * 100) / 100} CAD
                    that need to be settled, in addition to the cost of your new preferred package.
                  </p>
                </En>
                <Fa>
                  <p>
                    شما در حال حاضر {numberOfDebtSessions} جلسه پرداخت نشده معادل {Math.ceil(debt * 100) / 100} دلار
                    کانادا دارید که لازم است تسویه شوند. این مبلغ به هزینه بسته جدید انتخابی شما اضافه خواهد شد.
                  </p>
                </Fa>
              </Snack>
              <Space size="h-8" />
              <div className="flex flex-col gap-1">
                <RadioButton
                  name="payment-type"
                  label={
                    locale === "fa"
                      ? "کلاس من با این معلم به اتمام رسیده و مایلم فقط بدهی را پرداخت کنم."
                      : "The classes with this tutor have been completed, and I would like to settle the outstanding payment."
                  }
                  onChange={(e) => {
                    setIsPayingDebtOnly(true);
                    setNumberOfSessions(0); // Reset sessions to 0 when paying debt only
                    setPrice(debt);
                  }}
                  checked={isPayingDebtOnly}
                />
                <RadioButton
                  name="payment-type"
                  label={
                    locale === "fa"
                      ? "مایلم علاوه بر پرداخت بدهی، پکیج جدیدی نیز تهیه کنم."
                      : "I would like to get a new package, as well as paying off my debt."
                  }
                  onChange={(e) => {
                    setIsPayingDebtOnly(false);
                    setNumberOfSessions(20); // Default number of sessions
                  }}
                  checked={!isPayingDebtOnly}
                />
              </div>
            </>
          )}
          <Space size="h-4" />
          {!addedRecord && (
            <>
              <div
                className={`transition-all duration-300 ease-in-out overflow-hidden ${
                  debt > 0 && isPayingDebtOnly ? "opacity-0 max-h-0" : "opacity-100 max-h-[500px]"
                }`}
              >
                {(debt === 0 || !isPayingDebtOnly) && (
                  <>
                    <Select
                      label={locale === "fa" ? "شاگرد" : "Select the Student"}
                      name="course"
                      required
                      value={prevCourseQ.data
                        .filter((c) => c.isExpired === false)
                        .findIndex((pc) => pc.id === theCourse?.id)}
                      onChange={(val) => setTheCourse(prevCourseQ.data.filter((c) => c.isExpired === false)[val])}
                    >
                      {prevCourseQ.data
                        .filter((pc) => pc.isExpired === false)
                        .map((pc, i) => {
                          return (
                            <Select.Option key={pc.id} value={i}>
                              Students:{" "}
                              {Array.isArray(pc.courseStudent) &&
                                pc.courseStudent.map((cs) => cs.student.first_name).join(", ")}
                              {", "}
                              {pc.requestedSessionDurationInMinute} {"min"}
                            </Select.Option>
                          );
                        })}
                    </Select>

                    <Select
                      label={locale === "fa" ? "تعداد جلسات" : "Number of Sessions"}
                      name="numberOfSessions"
                      required
                      value={numberOfSessions}
                      onChange={(val) => {
                        setNumberOfSessions(val);
                        setIsPayingDebtOnly(false);
                      }}
                    >
                      <Select.Option value="1" dir={locale === "fa" ? "rtl" : "ltr"}>
                        {locale === "fa" ? "۱ (۱۵٪ به هزینه اضافه می‌شود)" : "1 (15% extra)"}
                      </Select.Option>
                      <Select.Option value="5" dir={locale === "fa" ? "rtl" : "ltr"}>
                        {locale === "fa" ? "۵" : "5"}
                      </Select.Option>
                      <Select.Option value="12" dir={locale === "fa" ? "rtl" : "ltr"}>
                        {locale === "fa" ? "۱۲" : "12"}
                      </Select.Option>
                      <Select.Option value="20" dir={locale === "fa" ? "rtl" : "ltr"}>
                        {locale === "fa" ? "۲۰ (۵٪ از هزینه کم می‌شود)" : "20 (5% discount)"}
                      </Select.Option>
                    </Select>
                    <div className="grid items-end w-full">
                      <div className="grid gap-1.5 items-end " style={{ gridTemplateColumns: "1fr auto" }}>
                        <Input
                          label={locale === "fa" ? "کد تخفیف" : "Coupon Code"}
                          type="text"
                          className=""
                          id="couponCode"
                          name="couponCode"
                          value={couponCode}
                          hasErrorMessage={false}
                          onChange={(e) => {
                            e.preventDefault();
                            setCouponCode(e.target.value);
                          }}
                        />
                        <Button variation="ghost" onClick={(e) => onApplyCoupon(e)}>
                          <Fa>اعمال کد</Fa>
                          <En>Apply Coupon</En>
                        </Button>
                      </div>
                      <p className={`${couponStatus.message === "Coupon is valid" ? "c-green11" : "c-red11"}`}>
                        {couponStatus.message}
                      </p>
                    </div>
                    <Space size="h-6" />
                  </>
                )}
              </div>
              <p>
                {locale === "fa" ? "قیمت کل" : "Total Cost"}:{" "}
                {debt > 0 && numberOfSessions > 0 ? `${price - debt} + ${debt}` : price} CAD
              </p>
              <Space size="h-6" />
              {errorHint && <p className="c-red11 mb-4">{errorHint}</p>}
              {paymentHint && <p className="c-yellow11 mb-4">{paymentHint}</p>}
              <div className="grid items-center h-full">
                <PaymentForm
                  applicationId={process.env.NEXT_PUBLIC_SQUARE_APPLICATION_ID!}
                  cardTokenizeResponseReceived={async (token, verifiedBuyer) => {
                    sendGTMEvent({ event: "pay-modal-click" });
                    setDisabledClosing(true);
                    setIsPayButtonLoading(true);
                    setPaymentHint(locale === "fa" ? "لطفاً صبر کنید" : "Please wait");
                    setErrorHint("");
                    try {
                      const response = await fetch("/api/pay", {
                        method: "POST",
                        headers: {
                          "Content-type": "application/json",
                        },
                        body: JSON.stringify({
                          sourceId: token.token,
                          amount: price,
                        }),
                      });
                      if (!response.ok) {
                        setErrorHint(
                          locale === "fa"
                            ? "با عرض پوزش، مشکلی در پرداخت شما وجود داشت. لطفاً مشخصات کارت خود را بررسی کنید و دوباره امتحان کنید. اگر مشکل همچنان ادامه داشت، لطفاً با تیم پشتیبانی ما تماس بگیرید."
                            : "We're sorry, but there was an issue processing your payment. Please check your card details and try again. If the problem persists, please contact our customer support team for assistance."
                        );
                        setPaymentHint("");
                        return;
                      }
                      const result = await response.json();
                      await handlePaymentSuccess(result.payment.orderId, result.payment.cardDetails.card.last4);
                      setPaymentHint("");
                    } catch (error) {
                      console.error("Payment processing error: ", error);
                      setPaymentHint("");
                    } finally {
                      setIsPayButtonLoading(undefined);
                      setDisabledClosing(false);
                    }
                  }}
                  locationId="L8YB0FFJPKQ4N"
                >
                  <CreditCard
                    buttonProps={{
                      isLoading: isPayButtonLoading,
                      css: {
                        backgroundColor: "#F76808",
                        fontSize: "14px",
                        color: "#fff",
                        "&:hover:not(:disabled)": {
                          backgroundColor: "#D85D07",
                        },
                      },
                    }}
                  >
                    <En>Pay</En>
                    <Fa>پرداخت</Fa>
                  </CreditCard>
                </PaymentForm>
              </div>
              <Snack variant="info" className="mt-4">
                <En>
                  If you want, you can send {debt > 0 && numberOfSessions > 0 ? `${price - debt} + ${debt}` : price}{" "}
                  dollars to Darsoon using e-transfer at <strong>darsoononline@gmail.com</strong>, and then share the
                  receipt on <a href="https://wa.me/18773703277">WhatsApp</a>.
                </En>
                <Fa>
                  همچنین شما می‌توانید در صورت تمایل از طریق ایترنسفر به آدرس darsoononline@gmail.com مبلغ{" "}
                  {debt > 0 && numberOfSessions > 0 ? `${price - debt} + ${debt}` : price} دلار را برای درسون واریز کرده
                  و سپس رسید واریز را در <a href="https://wa.me/18773703277">واتساپ</a> ارسال کنید.
                </Fa>
              </Snack>
            </>
          )}

          {addedRecord && (
            <Snack variant="success">
              {!isPayingDebtOnly && (
                <>
                  <En>
                    <p>Your package is successfully purchased.</p>
                    <p>Tutor: {getFullName(courseQ.data.teacher.user)}</p>
                    <p>Subject: {courseQ.data.topic.name}</p>
                    <p>Number of Sessions: {numberOfSessions}</p>
                    <p>Package Price: {paidTotal - paidDebt} CAD</p>
                    {paidDebt > 0 && <p>Debt Amount: {paidDebt} CAD</p>}
                  </En>
                  <Fa>
                    <p>خرید شما با موفقیت انجام شد.</p>
                    <p>معلم: {getFullNameFa(courseQ.data.teacher.user)}</p>
                    <p>موضوع: {courseQ.data.topic.nameFa}</p>
                    <p>تعداد جلسات: {numberOfSessions}</p>
                    <p>قیمت پکیج: {paidTotal - paidDebt} دلار کانادا</p>
                    {paidDebt > 0 && <p>مبلغ بدهی: {paidDebt} دلار کانادا</p>}
                  </Fa>
                </>
              )}
              {isPayingDebtOnly && (
                <>
                  <En>
                    <p>Your debt is successfully paid.</p>
                    <p>Amount: {paidDebt} CAD</p>
                  </En>
                  <Fa>
                    <p>بدهی شما با موفقیت پرداخت شد.</p>
                    <p>مبلغ: {paidDebt} دلار کانادا</p>
                  </Fa>
                </>
              )}
            </Snack>
          )}
        </>
      )}
    </>
  );
};

export default BookingModalContent;
